import React, { useMemo } from "react";
import { Container, Text } from "@pixi/react";
import { TextStyle, TextMetrics } from "pixi.js";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { formatToCustomLocale } from "../../../../utilities";
import { PixiSettings } from "../../../../pixiSettings";
import {
  FONT_CONFIG,
  USER_INTERFACE_BUTTON_CONFIG,
  TEXT_STYLE_CONFIG,
} from "../../../../textureConfigs";
import { selectDisplayedBalance, selectCurrency } from "../../../../State/selectors";

const BalanceDisplay = ({ toolbar }) => {
  const { t } = useTranslation();
  const { device, orientation, getResponsiveConfig, uiSymbolScale } =
    useDimensions();
  const displayedBalance = useSelector(selectDisplayedBalance);
  const currency = useSelector(selectCurrency);

  // Get button dimensions using the same system as PopiClock
  const buttonConfig = getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true);

  // Get unified balance configuration (font multiplier, y position)
  // X position now comes from toolbar.layout
  const getBalanceConfig = () => {
    let fontMultiplier = 1.0;
    let y = 0;

    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        fontMultiplier = 1.0;
        y = -buttonConfig.height * 0.9;
      } else {
        fontMultiplier = 1.1;
        y = toolbar.height / 2;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        fontMultiplier = 1.65;
        y = -buttonConfig.height * 0.9;
      } else {
        fontMultiplier = 1.25;
        y = toolbar.height / 2;
      }
    } else {
      // Desktop
      fontMultiplier = 1.0;
      y = toolbar.height / 2;
    }

    return { fontMultiplier, y };
  };

  const balanceConfig = getBalanceConfig();
  const baseFontSize = 32;
  const responsiveFontSize = baseFontSize * balanceConfig.fontMultiplier;
  const idealFontSize = Math.round(responsiveFontSize * uiSymbolScale);
  const LABEL_FONT_SIZE = Math.round(idealFontSize * 0.9); // Slightly smaller for label

  const balanceLabelStyle = new TextStyle({
    fontFamily: FONT_CONFIG.PRIMARY,
    fontSize: LABEL_FONT_SIZE,
    fontWeight: "500",
    fill: 0xffffff, // White color same as value
    align: "left",
    ...TEXT_STYLE_CONFIG.OUTLINE, // Black outline with 0.5 opacity and 2px thickness
  });

  const formattedBalance = formatToCustomLocale(displayedBalance);

  // Prepare text content
  const balanceLabelText = t("balance");
  const balanceAmountText = `${formattedBalance} ${currency}`;

  // Calculate maximum width based on 7-digit balance to stabilize component width
  // Use ideal font size for this calculation
  const maxBalanceWidth = useMemo(() => {
    const maxBalance = 9999999; // 7 digits
    const maxBalanceText = `${formatToCustomLocale(
      maxBalance
    )} ${currency}`;
    const tempStyle = new TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: idealFontSize,
      fontWeight: "700",
    });
    const maxBalanceMetrics = TextMetrics.measureText(
      maxBalanceText,
      tempStyle
    );

    return maxBalanceMetrics.width;
  }, [currency, idealFontSize]);

  /**
   * Auto-scaling font size calculation for balance amount
   * Similar to PopiBetButton: measures actual text and scales down if it exceeds max width
   * Only scales the amount, label stays fixed
   */
  const finalAmountFontSize = useMemo(() => {
    // Measure actual balance text with ideal font size
    const tempStyle = new TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: idealFontSize,
      fontWeight: "700",
    });
    const metrics = TextMetrics.measureText(balanceAmountText, tempStyle);

    // If text exceeds max width, scale down proportionally
    if (metrics.width > maxBalanceWidth) {
      const scale = maxBalanceWidth / metrics.width;
      return Math.round(idealFontSize * scale);
    }

    // Return ideal font size if it fits
    return idealFontSize;
  }, [balanceAmountText, idealFontSize, maxBalanceWidth]);

  const balanceAmountStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: finalAmountFontSize,
        fontWeight: "700", // Bigger font weight for value
        fill: 0xffffff, // White for numbers and currency
        align: "left",
        ...TEXT_STYLE_CONFIG.OUTLINE, // Black outline with 0.5 opacity and 2px thickness
      }),
    [finalAmountFontSize]
  );

  // Measure text dimensions for positioning - KEEPS CENTERED LABEL FEATURE
  const labelMetrics = TextMetrics.measureText(
    balanceLabelText,
    balanceLabelStyle
  );
  const amountMetrics = TextMetrics.measureText(
    balanceAmountText,
    balanceAmountStyle
  );

  const totalHeight = (labelMetrics.height + amountMetrics.height) * 0.95; // Reduce gap by 5%
  const maxWidth = Math.max(labelMetrics.width, maxBalanceWidth);
  const labelY = Math.round(-totalHeight / 2 + labelMetrics.height / 2);
  const amountY = Math.round(totalHeight / 2 - amountMetrics.height / 2);
  const textX = Math.round(maxWidth / 2);

  // Use layout position from toolbar hook if available
  const positionX = toolbar.layout?.balance?.x ?? buttonConfig.width * 0.5;

  return (
    <Container
      name="balance_display"
      x={positionX}
      y={balanceConfig.y}
      zIndex={5}
    >
      <Text
        key={`balance-label-column`}
        text={balanceLabelText}
        style={balanceLabelStyle}
        anchor={{ x: 0.5, y: 0.5 }}
        x={textX}
        y={labelY}
        resolution={2}
      />
      <Text
        key={`balance-amount-column`}
        text={balanceAmountText}
        style={balanceAmountStyle}
        anchor={{ x: 0.5, y: 0.5 }}
        x={textX}
        y={amountY}
        resolution={2}
      />
    </Container>
  );
};

export default BalanceDisplay;
